import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { calculateProgressPercentage, formatNumber } from "@/shared/utils";
import IconDonLinear from "@/assets/icons/don-linear.svg?react";
import IconShare from "@/assets/icons/share.svg?react";
import IconClose from "@/assets/icons/close.svg?react";
import { useCallback, useMemo, useState } from "react";
import { shareURL } from "@telegram-apps/sdk-react";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { ChallengeStatus } from "@/pages/challenges/types";

interface ModalShareChallengeProps {
  targetDon: number | string;
  gainedDon: number | string;
  referralCode: string;
  status: ChallengeStatus;
}

export default function ModalShareChallenge(props: ModalShareChallengeProps) {
  const { gainedDon, targetDon, referralCode, status } = props;
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (status === ChallengeStatus.COMING_SOON) {
        handleShareUrl();
      } else {
        setIsOpen(value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]
  );

  const miniAppUrl = `${
    import.meta.env.VITE_APP_TG_URL
  }?startapp=ref_${referralCode}`;

  const progressBar = useMemo(() => {
    return calculateProgressPercentage(
      targetDon.toString(),
      gainedDon.toString()
    );
  }, [targetDon, gainedDon]);

  const handleShareUrl = () => {
    if (shareURL.isAvailable()) {
      shareURL(miniAppUrl);
    }
  };

  const leftDon = useMemo(() => {
    return Math.max(
      parseFloat(targetDon.toString()) - parseFloat(gainedDon.toString()),
      0
    );
  }, [targetDon, gainedDon]);

  const progressText = useMemo(() => {
    if (progressBar === 0) {
      return t("you_need_dons_to_start_challenge");
    } else {
      return t("you_need_dons_to_continue_challenge");
    }
  }, [progressBar, t]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <VibratingButton className="size-11 flex-center rounded-xl bg-white/08 backdrop-blur-[20px] shrink-0">
          <IconShare />
        </VibratingButton>
      </DialogTrigger>

      <DialogContent className="p-4 bg-gray-linear rounded-20 max-w-[85vw] after:pointer-events-none after:z-0 after:absolute after:inset-0 after:border-2 after:border-white/08 after:rounded-20 border-none">
        <DialogClose className="absolute right-4 top-4 bg-white/08 rounded-full p-1.5">
          <IconClose />
        </DialogClose>

        <img
          className="absolute left-1/2 -translate-x-1/2 top-0 -translate-y-1/2 w-[200px] aspect-square pointer-events-none z-10"
          src="images/patterns/check-in-modal.webp"
          alt=""
        />

        <div className="relative z-10 flex flex-col items-center mt-10">
          <DialogTitle
            className={`flex-center gap-1 text-center text-white leading-130 font-extrabold self-stretch mb-1 ${
              leftDon === 0 ? "text-2xl" : "text-[28px]"
            }`}
          >
            {leftDon === 0 ? (
              t("all_don_collected")
            ) : (
              <>
                <IconDonLinear width="24" height="24" className="text-white" />
                {formatNumber(leftDon)}
              </>
            )}
          </DialogTitle>

          <p className="text-center text-white/80 font-normal leading-130 text-xs">
            {progressText}
          </p>

          <div className="w-full flex justify-between items-center mb-1">
            <div className="flex justify-between items-center w-full text-white text-sm font-semibold mt-3">
              <p className="leading-130 text-sm font-semibold">
                {formatNumber(
                  Math.min(
                    parseFloat(targetDon.toString()),
                    parseFloat(gainedDon.toString())
                  )
                )}{" "}
                <span className="text-gray-1 font-medium">
                  / {formatNumber(targetDon)}
                </span>
              </p>
              <p className="leading-130 text-sm font-semibold flex justify-center items-center gap-1">
                <span className="inline-block bg-white/[8%] text-xs leading-130 text-white rounded px-1 h-4">
                  {formatNumber(progressBar.toFixed(2))}%
                </span>
              </p>
            </div>
          </div>

          <div className="h-2 w-full rounded-lg bg-white/08 self-stretch mb-3">
            <div
              className={`h-full rounded-lg transition-all duration-300 ease-in-out ${
                progressBar === 100 ? "bg-orange-100" : "bg-blue"
              }`}
              style={{ width: `${progressBar}%` }}
            ></div>
          </div>

          <hr className="border-none bg-white/08 h-px w-full my-4" />

          <p className="text-center text-xs font-normal leading-130 text-white/80">
            {t("invite_friend_with_referral_link_and_challenge_start")}
          </p>

          <div className="w-full mt-4">
            <button
              className="py-2.5 w-full flex-center mx-auto flex-center gap-2 bg-blue rounded-xl text-sm font-semibold leading-140"
              onClick={handleShareUrl}
            >
              {t("share")}
              <IconShare className="text-white w-6 h-6" strokeWidth="1.5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
